import { useEffect } from 'react';
import {
  generatePath,
  useLocation,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { SendMessage } from 'react-use-websocket';
import queryString from 'query-string';
import { useAppSelector } from '@store';
import { IconSelected, PermissionEnum } from '@ui/src';
import { OrganizationType } from '../types';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import useAML from './hooks/use-aml';
import OngoingMonitoring from './parts/ongoing-monitoring-list';
import AmlGeneralInfo from '../../../components/common/aml/general-info';
import AdditionalInformation from '../../../components/common/aml/additional-information';
import Card from '../../../components/common/card/card';
import RiskScoring from '../../../components/common/aml/risk-scoring';
import OngoingMonitoringTable from './parts/ongoing-monitoring-table';
import InitialCDDContent from './parts/initial-cdd-content';
import AmlAccountsTransactionStatement from '../statements/aml';
import AmlPanels from './parts/panels';
import MonitoringRules from './parts/monitoring-rules';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const CustomerAML = ({ type }: { type: OrganizationType }) => {
  const {
    baseCurrency,
    cardsEnabled,
    currentEmployee,
    riskScoreRanges,
    applicationSchemes,
    monthlyTurnover,
    productsCorporate,
    productsIndividual,
    deliveryChannelsCorporate,
    deliveryChannelsIndividual,
    clientsActivities,
    sourcesOfFunds,
    sicCodesDictionaries,
  } = useAppSelector((state) => state.customersDictionaries);
  const { users } = useAppSelector((state) => state.administrationDictionaries);
  const {
    activePermissions,
    currenciesList: currencies,
    currencies: currenciesList,
    countriesList,
  } = useAppSelector((state) => state.root);
  const {
    baseInformation: individualBaseInformation,
    changeLogs: individualChangeLogs,
    changeLogsPagination: individualChangeLogsPagination,
  } = useAppSelector((state) => state.individualCustomers.info.general);
  const {
    baseInformation: corporateBaseInformation,
    businessPartners,
    changeLogs: corporateChangeLogs,
    changeLogsPagination: corporateChangeLogsPagination,
  } = useAppSelector((state) => state.corporateCustomers.info.general);
  const {
    aml,
    amlUpdateStatus,
    nextReviewDate,
    ongoingMonitoringItem,
    amlChangeLogs,
    amlChangeLogsPagination,
    expectedFundsOrigins,
    accountsCurrencies,
    accountsFlows,
    affiliatedCompanies,
    crsAndFatca,
    relationshipToTheCustomerDictionary,
    sourceOfWealth,
    businessActivities,
    customerMainClients,
    salesChannels,
    customerSourceOfFunds,
    customerSourceOfWealth,
    annualIncomes,
    typeOfAccounts,
    sourceOfIncome,
    sourceOfWealthForIndividual,
  } = useAppSelector((state) => state.customerAml);

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { customerId } = useParams();
  const { search } = useLocation();

  const navigate = useNavigate();

  const individualMatch = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_AML);
  const corporateMatch = useMatch(ROUTES.CUSTOMERS_CORPORATE_AML);

  const baseInformation =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;
  const changeLogs =
    type === OrganizationType.INDIVIDUAL
      ? individualChangeLogs
      : corporateChangeLogs;
  const changeLogsPagination =
    type === OrganizationType.INDIVIDUAL
      ? individualChangeLogsPagination
      : corporateChangeLogsPagination;

  const {
    setOngoingItemPanelVisible,
    onOngoingRowClick,
    ongoingItemIsFetching,
    ongoingItemIsLoading,
    setBlacklistDataVisible,
    setBlacklistData,
    setBlacklistDataItem,
    setBlacklistItemVisible,
    applyError,
    applyWarningInProgress,
    applyCustomerOngoingWarningAction,
    batchApplyWarningInProgress,
    batchApplyCustomerOngoingWarningAction,
    batchApplyWarningIsSuccess,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    selectedWarningId,
    selectedWarningGroup,
    getCustomerWarningsAction,
    paginationInProgress,
    paginationIsFetching,
    getCustomerOngoingWarningByIdAction,
    isWarningByIdLoading,
    isWarningByIdFetching,
    showOngoing,
    setShowOngoing,
    ongoingIsLoading,
    ongoingIsFetching,
    getCustomerOngoingMonitoring,
    setExportPdfPanelVisible,
    setChangeLogPanelIsOpen,
    questionnaireFields,
    control,
    currentApplicationScheme,
    defaultQuestionnaire,
    onResetQuestionnaire,
    onUpdateQuestionnaire,
    questionnnaireRequestInProgress,
    setValue,
    isLoading,
    onUpdate,
    requestInProgress,
    isSuccess,
    setRiskScoreChangeLogsSidepanelVisible,
    setManualScoreSidepanelVisible,
    cddFormValues,
    onCDDFormValuesChange,
    onSaveChanges,
    isDirty,
    updateRangesInProgress,
    successMessageVisible,
    exportPdfPanelVisible,
    ongoingMonitoringPdfIsLoading,
    getOngoingMonitoringPdfAction,
    exportDate,
    setExportDate,
    ongoingItemPanelVisible,
    setApplyError,
    blacklistDataVisible,
    blacklistData,
    blacklistItemVisible,
    blacklistItem,
    changeLogsPanelIsOpen,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    manualScoreSidepanelVisible,
    totalRiskScore,
    setTotalRiskScore,
    setManualRiskScoreError,
    setDirtyPopupVisible,
    manualRiskScoreError,
    createRiskScoreIsLoading,
    createCustomerManualRiskScoreAction,
    totalRiskScoreLevel,
    dirtyPopupVisible,
    riskScoreChangeLogsSidepanelVisible,
    getCustomerAmlChangeLogsAction,
    handleCloseAmlChangeLogsPanel,
    amlScoresHistoryIsFetching,
    amlScoresHistoryIsLoading,
    updateInfoIsLoading,
    setTotalRiskScoreLevel,
    defaultOrganizationValues,
    organizationFields,
    informationIsUpdate,
    errors,
    clearErrors,
    accountsCurrenciesIsLoading,
    accountsCurrenciesIsFetching,
    accountsCurrenciesValues,
    setAccountsCurrenciesValues,
    onDeleteAccountCurrencies,
    selectedAccountsCurrencyId,
    deleteAccountsCurrenciesInProgress,
    setAccountsFlowsValues,
    accountsFlowsValues,
    accountsFlowsIsLoading,
    accountsFlowsIsFetching,
    expectedFundsOriginsIsLoading,
    expectedFundsOriginsIsFetching,
    deleteExpectedFundsOriginsInProgress,
    onDeleteExpectedFundsOrigins,
    selectedExpectedFundsOriginsId,
    expectedFundsOriginsValues,
    setExpectedFundsOriginsValues,
    createAccountsCurrenciesIsProgress,
    updateAccountsCurrenciesInProgress,
    createAccountsFlowsIsProgress,
    updateAccountsFlowsInProgress,
    createExpectedFundsOriginsInProgress,
    updateExpectedFundsOriginsInProgress,
    selectedCrsAndFatcaId,
    crsAndFatcaIsLoading,
    crsAndFatcaIsFetching,
    createCrsAndFatcaIsProgress,
    updateCrsAndFatcaInProgress,
    deleteCrsAndFatcaInProgress,
    onDeleteCRSAndFatca,
    organizationControl,
    organizationErrors,
    organizationClearErrors,
    organizationSetValue,
    crsAndFatcaValues,
    setCrsAndFatcaValues,
    affilatedCompaniesValues,
    setAffilatedCompaniesValues,
    selectedAffilatedCompanyId,
    affilatedCompaniesIsLoading,
    affilatedCompaniesIsFetching,
    createAffilatedCompanyInProgress,
    updateAffilatedCompanyInProgress,
    deleteAffilatedCompanyInProgress,
    onDeleteAffilatedCompany,
    totalIncomeError,
    setTotalIncomeError,
  } = useAML({
    type,
    customerId,
    baseInformation,
    currentEmployee,
    applicationSchemes,
    amlUpdateStatus,
    activePermissions,
    aml,
    riskScoreRanges,
    expectedFundsOrigins,
    accountsCurrencies,
    accountsFlows,
    affiliatedCompanies,
    crsAndFatca,
    relationshipToTheCustomerDictionary,
    sourceOfWealth,
    businessActivities,
    customerMainClients,
    salesChannels,
    customerSourceOfFunds,
    customerSourceOfWealth,
    annualIncomes,
    typeOfAccounts,
    sourceOfIncome,
    sourceOfWealthForIndividual,
    clientsActivities,
    isCof: Boolean(baseInformation?.type === OrganizationType.COMPANY_OFFICIAL),
    isIndividual: Boolean(type === OrganizationType.INDIVIDUAL),
  });

  useEffect(() => {
    if (customerId) {
      if (corporateMatch) {
        navigate(
          generatePath(ROUTES.CUSTOMERS_CORPORATE_AML_TRANSACTIONS, {
            customerId,
          }),
        );
      }

      if (individualMatch) {
        navigate(
          generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_AML_TRANSACTIONS, {
            customerId,
          }),
        );
      }
    }
  }, [customerId, corporateMatch, individualMatch]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.ongoingMonitoringItemId) {
        setOngoingItemPanelVisible(true);

        onOngoingRowClick(parsed.ongoingMonitoringItemId as string);
      }
    }
  }, [search]);

  useEffect(() => {
    let title = '';

    if (type === OrganizationType.INDIVIDUAL) {
      title =
        (baseInformation as Partial<GeneralInfo>)?.firstName ||
        (baseInformation as Partial<GeneralInfo>)?.lastName
          ? `${(baseInformation as Partial<GeneralInfo>)?.firstName} ${(baseInformation as Partial<GeneralInfo>)?.lastName || ''}: AML & KYC`
          : 'AML & KYC';
    } else if (type === OrganizationType.CORPORATE) {
      title = (baseInformation as Partial<CorporateGeneralInfo>)?.name
        ? `${(baseInformation as Partial<CorporateGeneralInfo>)?.name}: AML & KYC`
        : 'AML & KYC';
    }

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation, type]);

  let amlContent;

  if (
    showOngoing ||
    (type === OrganizationType.INDIVIDUAL &&
      (baseInformation as Partial<GeneralInfo>)?.type ===
        OrganizationType.COMPANY_OFFICIAL)
  ) {
    amlContent = (
      <>
        {type === OrganizationType.INDIVIDUAL &&
          (baseInformation as Partial<GeneralInfo>)?.type ===
            OrganizationType.COMPANY_OFFICIAL &&
          baseInformation?.questionnaire &&
          Object.keys(baseInformation?.questionnaire)?.length > 0 && (
            <AdditionalInformation
              questionnaire={baseInformation?.questionnaire}
              control={
                (baseInformation?.type === OrganizationType.INDIVIDUAL &&
                  currentApplicationScheme?.id ===
                    'ed560559-8354-4634-b6dd-eb341dfb551a') ||
                (baseInformation?.type === OrganizationType.COMPANY_OFFICIAL &&
                  currentApplicationScheme?.id ===
                    'a87db4b4-3c78-4564-bba1-c16a19553159') ||
                (baseInformation?.type === OrganizationType.CORPORATE &&
                  (questionnaireFields?.hasRegulatoryInvestigation ||
                    currentApplicationScheme?.id ===
                      'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'))
                  ? control
                  : undefined
              }
              setValue={setValue}
              currentApplicationScheme={currentApplicationScheme}
              questionnaireFields={questionnaireFields}
              isDirty={
                currentApplicationScheme?.id !==
                  'ed560559-8354-4634-b6dd-eb341dfb551a' &&
                currentApplicationScheme?.id !==
                  'a87db4b4-3c78-4564-bba1-c16a19553159' &&
                currentApplicationScheme?.id !==
                  'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
                  ? !isEqual(defaultQuestionnaire, questionnaireFields) ||
                    !isEqual(defaultOrganizationValues, organizationFields)
                  : true
              }
              onResetQuestionnaire={onResetQuestionnaire}
              onUpdateQuestionnaire={onUpdateQuestionnaire}
              requestInProgress={
                questionnnaireRequestInProgress || informationIsUpdate
              }
              status={baseInformation?.status}
              errors={errors}
              clearErrors={clearErrors}
              countries={countriesList}
              baseCurrency={baseCurrency}
              accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
              accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
              accountsCurrenciesValues={accountsCurrenciesValues}
              setAccountsCurrenciesValues={setAccountsCurrenciesValues}
              currencies={currencies}
              onDeleteAccountCurrencies={onDeleteAccountCurrencies}
              selectedAccountsCurrencyId={selectedAccountsCurrencyId}
              deleteAccountsCurrenciesInProgress={
                deleteAccountsCurrenciesInProgress
              }
              setAccountsFlowsValues={setAccountsFlowsValues}
              accountsFlowsValues={accountsFlowsValues}
              accountsFlowsIsLoading={accountsFlowsIsLoading}
              accountsFlowsIsFetching={accountsFlowsIsFetching}
              currenciesList={currenciesList}
              expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
              expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
              deleteExpectedFundsOriginsInProgress={
                deleteExpectedFundsOriginsInProgress
              }
              onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
              selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
              expectedFundsOriginsValues={expectedFundsOriginsValues}
              setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
              createAccountsCurrenciesIsProgress={
                createAccountsCurrenciesIsProgress
              }
              updateAccountsCurrenciesInProgress={
                updateAccountsCurrenciesInProgress
              }
              createAccountsFlowsIsProgress={createAccountsFlowsIsProgress}
              updateAccountsFlowsInProgress={updateAccountsFlowsInProgress}
              createExpectedFundsOriginsInProgress={
                createExpectedFundsOriginsInProgress
              }
              updateExpectedFundsOriginsInProgress={
                updateExpectedFundsOriginsInProgress
              }
              sourceOfWealth={sourceOfWealth}
              sourceOfIncome={sourceOfIncome}
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              customerSourceOfFunds={customerSourceOfFunds}
              selectedCrsAndFatcaId={selectedCrsAndFatcaId}
              crsAndFatcaIsLoading={crsAndFatcaIsLoading}
              crsAndFatcaIsFetching={crsAndFatcaIsFetching}
              createCrsAndFatcaIsProgress={createCrsAndFatcaIsProgress}
              updateCrsAndFatcaInProgress={updateCrsAndFatcaInProgress}
              deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
              onDeleteCRSAndFatca={onDeleteCRSAndFatca}
              organizationControl={organizationControl}
              organizationErrors={organizationErrors}
              organizationClearErrors={organizationClearErrors}
              organizationFields={organizationFields}
              organizationSetValue={organizationSetValue}
              crsAndFatcaValues={crsAndFatcaValues}
              setCrsAndFatcaValues={setCrsAndFatcaValues}
              isCof={
                baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
              }
              affilatedCompaniesValues={affilatedCompaniesValues}
              setAffilatedCompaniesValues={setAffilatedCompaniesValues}
              selectedAffilatedCompanyId={selectedAffilatedCompanyId}
              affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
              affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
              createAffilatedCompanyInProgress={
                createAffilatedCompanyInProgress
              }
              updateAffilatedCompanyInProgress={
                updateAffilatedCompanyInProgress
              }
              deleteAffilatedCompanyInProgress={
                deleteAffilatedCompanyInProgress
              }
              onDeleteAffilatedCompany={onDeleteAffilatedCompany}

              relationshipToTheCustomerDictionary={
                relationshipToTheCustomerDictionary
              }
              businessActivities={businessActivities}
              customerMainClients={customerMainClients}
              salesChannels={salesChannels}
              customerSourceOfWealth={customerSourceOfWealth}
              annualIncomes={annualIncomes}
              totalIncomeError={totalIncomeError}
              setTotalIncomeError={setTotalIncomeError}
              typeOfAccounts={typeOfAccounts}
              type={type}
            />
          )}
        <OngoingMonitoring
          setShowOngoing={setShowOngoing}
          onRowClick={onOngoingRowClick}
          isLoading={ongoingIsLoading || ongoingIsFetching}
          type={baseInformation?.type as OrganizationType}
          getOngoingMonitroing={({ id, take, cursor }) =>
            getCustomerOngoingMonitoring({
              id,
              take: take || '',
              cursor: cursor || '',
            })
          }
          setExportPdfPanelVisible={setExportPdfPanelVisible}
          exportAvailable={
            type === OrganizationType.INDIVIDUAL
              ? activePermissions?.includes(
                  PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_EXPORT_ACTION,
                )
              : activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_AML_EXPORT_ACTION,
                )
          }
        />
      </>
    );
  } else {
    amlContent = (
      <>
        <AmlGeneralInfo
          isLoading={isLoading}
          baseInformation={baseInformation}
          nextReviewDate={nextReviewDate}
          auditLogAllowed={activePermissions?.includes(
            PermissionEnum.AUDIT_LOG_LIST,
          )}
          setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
          clientsActivities={clientsActivities}
          type={type}
        />
        {baseInformation?.questionnaire &&
          Object.keys(baseInformation?.questionnaire)?.length > 0 && (
            <AdditionalInformation
              questionnaire={baseInformation?.questionnaire}
              control={
                (baseInformation?.type === OrganizationType.INDIVIDUAL &&
                  currentApplicationScheme?.id ===
                    'ed560559-8354-4634-b6dd-eb341dfb551a') ||
                (baseInformation?.type === OrganizationType.COMPANY_OFFICIAL &&
                  currentApplicationScheme?.id ===
                    'a87db4b4-3c78-4564-bba1-c16a19553159') ||
                (baseInformation?.type === OrganizationType.CORPORATE &&
                  (questionnaireFields?.hasRegulatoryInvestigation ||
                    currentApplicationScheme?.id ===
                      'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'))
                  ? control
                  : undefined
              }
              setValue={setValue}
              currentApplicationScheme={currentApplicationScheme}
              questionnaireFields={questionnaireFields}
              isDirty={
                currentApplicationScheme?.id !==
                  'ed560559-8354-4634-b6dd-eb341dfb551a' &&
                currentApplicationScheme?.id !==
                  'a87db4b4-3c78-4564-bba1-c16a19553159' &&
                currentApplicationScheme?.id !==
                  'cdbb8c71-90e6-4a5e-964f-e55169e0c4df'
                  ? !isEqual(defaultQuestionnaire, questionnaireFields) ||
                    !isEqual(defaultOrganizationValues, organizationFields)
                  : true
              }
              onResetQuestionnaire={onResetQuestionnaire}
              onUpdateQuestionnaire={onUpdateQuestionnaire}
              requestInProgress={
                questionnnaireRequestInProgress || informationIsUpdate
              }
              status={baseInformation?.status}
              errors={errors}
              clearErrors={clearErrors}
              countries={countriesList}
              baseCurrency={baseCurrency}
              accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
              accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
              accountsCurrenciesValues={accountsCurrenciesValues}
              setAccountsCurrenciesValues={setAccountsCurrenciesValues}
              currencies={currencies}
              onDeleteAccountCurrencies={onDeleteAccountCurrencies}
              selectedAccountsCurrencyId={selectedAccountsCurrencyId}
              deleteAccountsCurrenciesInProgress={
                deleteAccountsCurrenciesInProgress
              }
              setAccountsFlowsValues={setAccountsFlowsValues}
              accountsFlowsValues={accountsFlowsValues}
              accountsFlowsIsLoading={accountsFlowsIsLoading}
              accountsFlowsIsFetching={accountsFlowsIsFetching}
              currenciesList={currenciesList}
              expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
              expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
              deleteExpectedFundsOriginsInProgress={
                deleteExpectedFundsOriginsInProgress
              }
              onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
              selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
              expectedFundsOriginsValues={expectedFundsOriginsValues}
              setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
              createAccountsCurrenciesIsProgress={
                createAccountsCurrenciesIsProgress
              }
              updateAccountsCurrenciesInProgress={
                updateAccountsCurrenciesInProgress
              }
              createAccountsFlowsIsProgress={createAccountsFlowsIsProgress}
              updateAccountsFlowsInProgress={updateAccountsFlowsInProgress}
              createExpectedFundsOriginsInProgress={
                createExpectedFundsOriginsInProgress
              }
              updateExpectedFundsOriginsInProgress={
                updateExpectedFundsOriginsInProgress
              }
              sourceOfWealth={sourceOfWealth}
              sourceOfIncome={sourceOfIncome}
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              customerSourceOfFunds={customerSourceOfFunds}
              selectedCrsAndFatcaId={selectedCrsAndFatcaId}
              crsAndFatcaIsLoading={crsAndFatcaIsLoading}
              crsAndFatcaIsFetching={crsAndFatcaIsFetching}
              createCrsAndFatcaIsProgress={createCrsAndFatcaIsProgress}
              updateCrsAndFatcaInProgress={updateCrsAndFatcaInProgress}
              deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
              onDeleteCRSAndFatca={onDeleteCRSAndFatca}
              organizationControl={organizationControl}
              organizationErrors={organizationErrors}
              organizationClearErrors={organizationClearErrors}
              organizationFields={organizationFields}
              organizationSetValue={organizationSetValue}
              crsAndFatcaValues={crsAndFatcaValues}
              setCrsAndFatcaValues={setCrsAndFatcaValues}
              isCof={
                baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
              }
              affilatedCompaniesValues={affilatedCompaniesValues}
              setAffilatedCompaniesValues={setAffilatedCompaniesValues}
              selectedAffilatedCompanyId={selectedAffilatedCompanyId}
              affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
              affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
              createAffilatedCompanyInProgress={
                createAffilatedCompanyInProgress
              }
              updateAffilatedCompanyInProgress={
                updateAffilatedCompanyInProgress
              }
              deleteAffilatedCompanyInProgress={
                deleteAffilatedCompanyInProgress
              }
              onDeleteAffilatedCompany={onDeleteAffilatedCompany}

              relationshipToTheCustomerDictionary={
                relationshipToTheCustomerDictionary
              }
              businessActivities={businessActivities}
              customerMainClients={customerMainClients}
              salesChannels={salesChannels}
              customerSourceOfWealth={customerSourceOfWealth}
              annualIncomes={annualIncomes}
              totalIncomeError={totalIncomeError}
              setTotalIncomeError={setTotalIncomeError}
              typeOfAccounts={typeOfAccounts}
              type={type}
            />
          )}
        {((type === OrganizationType.INDIVIDUAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
          )) ||
          (type === OrganizationType.CORPORATE &&
            activePermissions?.includes(
              PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST,
            ))) && (
          <Card
            title="Risk scoring"
            isLoading={isLoading}
            className={cn.riskScoringCard}
          >
            <RiskScoring
              aml={aml}
              onUpdate={onUpdate}
              updateInProgress={requestInProgress}
              updateIsSuccess={isSuccess}
              amlUpdateStatus={amlUpdateStatus}
              calculateAllowed={
                type === OrganizationType.INDIVIDUAL
                  ? activePermissions?.includes(
                      PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_AUTO_ON_ACTION,
                    )
                  : activePermissions?.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_SCORE_AUTO_ON_ACTION,
                    )
              }
              status={baseInformation?.status}
              isLoading={isLoading}
              users={users}
              manualScoreAllowed={
                type === OrganizationType.INDIVIDUAL
                  ? activePermissions?.includes(
                      PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_CREATE_ACTION,
                    )
                  : activePermissions?.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_SCORE_CREATE_ACTION,
                    )
              }
              setRiskScoreChangeLogsSidepanelVisible={
                setRiskScoreChangeLogsSidepanelVisible
              }
              setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
              monthlyTurnover={monthlyTurnover}
              countries={countriesList}
              activePermissions={activePermissions}
              type={type}
              clientsActivities={clientsActivities}
              individualDeliveryChannels={deliveryChannelsIndividual}
              individualProducts={productsIndividual}
              sourcesOfFunds={sourcesOfFunds}
              corporateDeliveryChannels={deliveryChannelsCorporate}
              corporateProducts={productsCorporate}
              sicCodesDictionaries={sicCodesDictionaries}
              riskScoreRanges={riskScoreRanges}
            />
          </Card>
        )}
        {((type === OrganizationType.INDIVIDUAL &&
          activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST,
          )) ||
          (type === OrganizationType.CORPORATE &&
            activePermissions?.includes(
              PermissionEnum.CORPORATE_CUSTOMERS_AML_LIST,
            ))) && (
          <OngoingMonitoringTable
            activePermissions={activePermissions}
            setShowOngoing={setShowOngoing}
            setExportPdfPanelVisible={setExportPdfPanelVisible}
            ongoingIsLoading={ongoingIsLoading}
            ongoingIsFetching={ongoingIsFetching}
            onOngoingRowClick={onOngoingRowClick}
            type={type}
          />
        )}
        {activePermissions?.includes(
          PermissionEnum.AML_MONITORING_RULES_LIST,
        ) && (
          <MonitoringRules
            activePermissions={activePermissions}
            customerId={customerId}
            type={type}
          />
        )}
        <Card isLoading={isLoading} title="Initial CDD">
          <InitialCDDContent
            type={type}
            baseCurrency={baseCurrency}
            cddFormValues={cddFormValues}
            onChange={onCDDFormValuesChange}
            onSaveChanges={onSaveChanges}
            status={baseInformation?.status}
            isDirty={isDirty}
            updateRangesInProgress={
              updateRangesInProgress || updateInfoIsLoading
            }
            businessPartners={businessPartners}
            clientScheme={currentApplicationScheme}
            currencies={currencies}
          />
        </Card>
        <AmlAccountsTransactionStatement
          title="Red flag history"
          matchPath={
            type === OrganizationType.INDIVIDUAL
              ? ROUTES.CUSTOMERS_INDIVIDUAL_AML_CARDS
              : ROUTES.CUSTOMERS_CORPORATE_AML_CARDS
          }
          transactionsPath={
            type === OrganizationType.INDIVIDUAL
              ? ROUTES.CUSTOMERS_INDIVIDUAL_AML_TRANSACTIONS
              : ROUTES.CUSTOMERS_CORPORATE_AML_TRANSACTIONS
          }
          cardsTransactionsPath={
            type === OrganizationType.INDIVIDUAL
              ? ROUTES.CUSTOMERS_INDIVIDUAL_AML_CARDS
              : ROUTES.CUSTOMERS_CORPORATE_AML_CARDS
          }
          customerType={
            type === OrganizationType.INDIVIDUAL
              ? OrganizationType.INDIVIDUAL
              : OrganizationType.CORPORATE
          }
          accounts={baseInformation?.accounts}
          cards={baseInformation?.cards}
          cardsEnabled={cardsEnabled}
          sendMessage={sendMessage}
        />
      </>
    );
  }

  return (
    <div className={cn.amlContent}>
      {amlContent}
      {successMessageVisible && (
        <div className={cn.successAppliedMessage}>
          <IconSelected />
          <div>Risk Score successfully updated to the Customer</div>
        </div>
      )}
      <AmlPanels
        exportPdfPanelVisible={exportPdfPanelVisible}
        setExportPdfPanelVisible={setExportPdfPanelVisible}
        ongoingMonitoringPdfIsLoading={ongoingMonitoringPdfIsLoading}
        getOngoingMonitoringPdfAction={getOngoingMonitoringPdfAction}
        exportDate={exportDate}
        setExportDate={setExportDate}
        ongoingItemIsFetching={ongoingItemIsFetching}
        ongoingItemIsLoading={ongoingItemIsLoading}
        ongoingMonitoringItem={ongoingMonitoringItem}
        setOngoingItemPanelVisible={setOngoingItemPanelVisible}
        setBlacklistDataVisible={setBlacklistDataVisible}
        setBlacklistData={setBlacklistData}
        setBlacklistDataItem={setBlacklistDataItem}
        setBlacklistItemVisible={setBlacklistItemVisible}
        type={type}
        applyError={applyError}
        applyWarningInProgress={applyWarningInProgress}
        applyCustomerOngoingWarningAction={applyCustomerOngoingWarningAction}
        batchApplyWarningInProgress={batchApplyWarningInProgress}
        batchApplyCustomerOngoingWarningAction={
          batchApplyCustomerOngoingWarningAction
        }
        batchApplyWarningIsSuccess={batchApplyWarningIsSuccess}
        onResetTransactionWarningAction={onResetTransactionWarningAction}
        resetTransactionsWarningIsLoading={resetTransactionsWarningIsLoading}
        resetTransactionsWarningIsSuccess={resetTransactionsWarningIsSuccess}
        selectedWarningId={selectedWarningId}
        status={baseInformation?.status}
        selectedWarningGroup={selectedWarningGroup}
        getCustomerWarningsAction={getCustomerWarningsAction}
        paginationInProgress={paginationInProgress}
        paginationIsFetching={paginationIsFetching}
        getCustomerOngoingWarningByIdAction={
          getCustomerOngoingWarningByIdAction
        }
        isWarningByIdLoading={isWarningByIdLoading}
        isWarningByIdFetching={isWarningByIdFetching}
        ongoingItemPanelVisible={ongoingItemPanelVisible}
        setApplyError={setApplyError}
        blacklistDataVisible={blacklistDataVisible}
        blacklistData={blacklistData}
        blacklistItemVisible={blacklistItemVisible}
        blacklistItem={blacklistItem}
        changeLogsPanelIsOpen={changeLogsPanelIsOpen}
        setChangeLogPanelIsOpen={setChangeLogPanelIsOpen}
        activePermissions={activePermissions}
        customerId={customerId}
        changeLogsIsLoading={changeLogsIsLoading}
        changeLogs={changeLogs}
        users={users}
        changeLogsPagination={changeLogsPagination}
        changeLogsIsFetching={changeLogsIsFetching}
        getChangeLogsAction={getChangeLogsAction}
        manualScoreSidepanelVisible={manualScoreSidepanelVisible}
        aml={aml}
        totalRiskScore={totalRiskScore}
        setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
        setTotalRiskScore={setTotalRiskScore}
        setTotalRiskScoreLevel={setTotalRiskScoreLevel}
        setManualRiskScoreError={setManualRiskScoreError}
        setDirtyPopupVisible={setDirtyPopupVisible}
        manualRiskScoreError={manualRiskScoreError}
        createRiskScoreIsLoading={createRiskScoreIsLoading}
        createCustomerManualRiskScoreAction={
          createCustomerManualRiskScoreAction
        }
        totalRiskScoreLevel={totalRiskScoreLevel}
        riskScoreRanges={riskScoreRanges}
        dirtyPopupVisible={dirtyPopupVisible}
        riskScoreChangeLogsSidepanelVisible={
          riskScoreChangeLogsSidepanelVisible
        }
        getCustomerAmlChangeLogsAction={getCustomerAmlChangeLogsAction}
        amlChangeLogs={amlChangeLogs}
        handleCloseAmlChangeLogsPanel={handleCloseAmlChangeLogsPanel}
        amlChangeLogsIsLoading={amlScoresHistoryIsLoading}
        amlScoresHistoryIsFetching={amlScoresHistoryIsFetching}
        amlChangeLogsPagination={amlChangeLogsPagination}
      />

      <div id="dirty-popup" />
      <div id="apply-all-popup" />
      <div id="signature-popup" />
    </div>
  );
};

export default CustomerAML;
